import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';

// ═══════════════════════════════════════════════════════════════
// OnboardingWizard — 5-step onboarding wizard (placeholder)
// Forms don't save to backend yet. Visual placeholder only.
// ═══════════════════════════════════════════════════════════════

const CARD: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  padding: '32px 40px',
  maxWidth: 640,
  width: '100%',
};

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  marginBottom: 6,
};

export function OnboardingWizard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const steps = t.onboarding.steps;

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#F0F2F5',
        fontFamily: 'Montserrat, sans-serif',
        padding: '48px 24px',
      }}
    >
      {/* Stepper */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          marginBottom: 40,
          maxWidth: 640,
          width: '100%',
        }}
      >
        {steps.map((_label, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: i < steps.length - 1 ? 1 : 'none',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
                backgroundColor: i <= step ? '#1F3864' : '#E5E7EB',
                color: i <= step ? '#fff' : '#9CA3AF',
                transition: 'all 0.3s',
              }}
            >
              {i < step ? '\u2713' : i + 1}
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 3,
                  backgroundColor: i < step ? '#1F3864' : '#E5E7EB',
                  transition: 'background-color 0.3s',
                  margin: '0 4px',
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step label */}
      <p
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#6B7280',
          marginBottom: 8,
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
      >
        {t.onboarding.stepLabel(step + 1, steps.length)}
      </p>

      {/* Step content */}
      <div style={CARD}>
        {step === 0 && <StepBienvenida t={t} />}
        {step === 1 && <StepEmpresa t={t} />}
        {step === 2 && <StepLogoMarca t={t} />}
        {step === 3 && <StepUsuarios t={t} />}
        {step === 4 && <StepCompletado t={t} onFinish={() => navigate('/admin')} />}
      </div>

      {/* Navigation */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 24,
          maxWidth: 640,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={back}
          disabled={step === 0}
          style={{
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            border: '1px solid #D1D5DB',
            borderRadius: 8,
            backgroundColor: '#fff',
            color: step === 0 ? '#D1D5DB' : '#374151',
            cursor: step === 0 ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {t.common.anterior}
        </button>
        {step < 4 ? (
          <button
            onClick={next}
            style={{
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              border: 'none',
              borderRadius: 8,
              backgroundColor: '#1F3864',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t.common.siguiente}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Step components ────────────────────────────────────────────────────────

function StepBienvenida({ t }: { t: any }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>&#128075;</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1F3864', marginBottom: 12 }}>
        {t.onboarding.welcomeTitle}
      </h2>
      <p style={{ fontSize: 15, color: '#6B7280', lineHeight: 1.6 }}>{t.onboarding.welcomeDesc}</p>
    </div>
  );
}

function StepEmpresa({ t }: { t: any }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', marginBottom: 20 }}>
        {t.onboarding.companyTitle}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={LABEL}>{t.onboarding.companyName}</label>
          <input type="text" style={INPUT} placeholder={t.onboarding.companyNamePlaceholder} />
        </div>
        <div>
          <label style={LABEL}>{t.onboarding.industry}</label>
          <input type="text" style={INPUT} placeholder={t.onboarding.industryPlaceholder} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={LABEL}>{t.onboarding.country}</label>
            <input type="text" style={INPUT} placeholder={t.onboarding.countryPlaceholder} />
          </div>
          <div>
            <label style={LABEL}>{t.onboarding.employees}</label>
            <input type="number" style={INPUT} placeholder="50" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepLogoMarca({ t }: { t: any }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', marginBottom: 20 }}>
        {t.onboarding.brandTitle}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={LABEL}>{t.onboarding.logo}</label>
          <div
            style={{
              border: '2px dashed #D1D5DB',
              borderRadius: 12,
              padding: 32,
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t.onboarding.dropLogo}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={LABEL}>{t.onboarding.primaryColor}</label>
            <input
              type="color"
              defaultValue="#1F3864"
              style={{
                width: '100%',
                height: 40,
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            />
          </div>
          <div>
            <label style={LABEL}>{t.onboarding.secondaryColor}</label>
            <input
              type="color"
              defaultValue="#2F5496"
              style={{
                width: '100%',
                height: 40,
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            />
          </div>
          <div>
            <label style={LABEL}>{t.onboarding.accentColor}</label>
            <input
              type="color"
              defaultValue="#33BCE1"
              style={{
                width: '100%',
                height: 40,
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepUsuarios({ t }: { t: any }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', marginBottom: 20 }}>
        {t.onboarding.usersTitle}
      </h2>
      <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>{t.onboarding.usersDesc}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: 8,
              alignItems: 'end',
            }}
          >
            <div>
              <label style={LABEL}>{t.onboarding.email}</label>
              <input type="email" style={INPUT} placeholder={`usuario${i}@empresa.com`} />
            </div>
            <div>
              <label style={LABEL}>{t.onboarding.role}</label>
              <select style={INPUT}>
                <option value="ADMINISTRADOR">Administrador</option>
                <option value="EDITOR">Editor</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepCompletado({ t, onFinish }: { t: any; onFinish: () => void }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>&#127881;</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1F3864', marginBottom: 12 }}>
        {t.onboarding.completeTitle}
      </h2>
      <p style={{ fontSize: 15, color: '#6B7280', lineHeight: 1.6, marginBottom: 24 }}>
        {t.onboarding.completeDesc}
      </p>
      <button
        onClick={onFinish}
        style={{
          padding: '12px 32px',
          fontSize: 15,
          fontWeight: 600,
          border: 'none',
          borderRadius: 8,
          backgroundColor: '#1F3864',
          color: '#fff',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {t.onboarding.goToPanel}
      </button>
    </div>
  );
}
